import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BookingDto, SeatBookingInput } from '@gc/shared';

import type { LiveTabSummary } from '@/features/tabs/liveTabs';
import { api, ApiError } from '@/lib/api';
import { partyName } from './bookingLogic';

interface SeatBookingDialogProps {
  booking: BookingDto;
  onClose: () => void;
  onSeated: () => void;
}

/**
 * Seat the party: start their session under a fresh tab (the default), or
 * attach the device to a tab already running — the group that booked a second
 * machine mid-evening. `device_busy` comes back when a walk-in ran long; the
 * booking stays BOOKED, so seating again after the machine frees up just works.
 */
export function SeatBookingDialog({ booking, onClose, onSeated }: SeatBookingDialogProps) {
  const { t } = useTranslation();
  const [openTabs, setOpenTabs] = useState<LiveTabSummary[]>([]);
  const [tabId, setTabId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The LIVE tabs, not every OPEN one: a party that owes nothing, runs nothing
  // and holds no pass is a paid-up tab nobody closed, and seating a booking on
  // it brings it back to life beside the party's real one (owner, 2026-09-23:
  // two 'Yaran staff' in the cafe's search, one in Outstanding). Same list,
  // same rule, as every other open-tab picker.
  useEffect(() => {
    void (async () => {
      try {
        setOpenTabs(await api.get<LiveTabSummary[]>('/tabs/live'));
      } catch {
        setOpenTabs([]);
      }
    })();
  }, []);

  async function handleSeat(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const payload: SeatBookingInput = tabId ? { tabId } : {};
      await api.post(`/bookings/${booking.id}/seat`, payload);
      onSeated();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'device_busy') {
        setError(t('bookings.seatDialog.deviceBusy', { device: booking.deviceName }));
      } else {
        setError(caught instanceof ApiError ? caught.message : t('bookings.errors.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('bookings.seatDialog.title', { name: partyName(booking) })}
        className="fixed inset-0 z-50 grid place-items-center p-4"
      >
        <div className="w-full max-w-sm rounded-card border border-subtle bg-ground p-4 shadow-2xl">
          <h2 className="text-base font-semibold text-content">
            {t('bookings.seatDialog.title', { name: partyName(booking) })}
          </h2>
          <p className="mt-1 text-sm text-content-secondary">
            {t('bookings.seatDialog.subtitle', {
              device: booking.deviceName,
              minutes: booking.durationMinutes,
            })}
          </p>

          <label className="mt-3 block">
            <span className="mb-1 block text-xs text-content-secondary">
              {t('bookings.seatDialog.tab')}
            </span>
            <select
              value={tabId}
              onChange={(event) => setTabId(event.target.value)}
              className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary"
            >
              <option value="">{t('bookings.seatDialog.newTab')}</option>
              {openTabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.name} · {tab.deviceCount}
                </option>
              ))}
            </select>
          </label>

          {error ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-4 py-1.5 text-sm text-content-secondary hover:bg-hover hover:text-content"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void handleSeat()}
              disabled={submitting}
              className="rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60"
            >
              {t('bookings.seatDialog.confirm')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
